import { cellAtWorld, cellCenter, geo, heroWalkable } from '../grid.ts';
import { cosA, dist2, iatan2, idiv, imax, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, CamsRules, CamsState, Enemy, Hero, SendaNpc } from '../types.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { useSkills } from './ghost.ts';
import { inArc, turnOf } from '../battle/golems.ts';
import { ventAt, ventBlowing, ventWarned } from '../battle/hondo.ts';
import { CAM_LEVER, camOrder, camSet } from '../battle/cams.ts';
import { CAM_FACES, camsTurns } from '../battle/corazon-layout.ts';
import { tenazaGrips } from '../battle/tenaza.ts';

/**
 * The lab's bot for the artificer's chapter 3 (docs/sendas/artifice.md, «El corazón»; the common bot in
 * ai/senda-bot.ts hands these acts here). It only moves the hero and picks its target, as a player's commands would.
 *
 * - the deep road (`vent`): who knows keeps by Grillo (he knows the beat) and crosses with him, steps out of a band
 *   warned or blowing, and shoots what is within reach without walking into a band; who doesn't walks down the road
 *   after what is nearest, the beat paid no mind.
 * - the river of embers (`ember`): who knows keeps by Dunna, strikes what comes for her first, and steps off a spot
 *   of magma warned under it; who doesn't fights what is nearest wherever it is.
 * - the cams (`cams`, the test): who knows splits the veins' order into the runs that go the shaft's way and sets one
 *   turn at a time (a cam off a wrong place first, the place it needs next), lets the hammer go, and between turns, or
 *   while the crab holds the wheel, shoots La Tenaza from its side; it steps out of the pincers and off the magma
 *   unless a cam is half moved. Who doesn't sets the cams on the first faces of the order all at once and lets go, and
 *   again the same way.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

/** Shoots `t` from where it stands (no walking after it). */
const shoot = (s: BattleState, h: Hero, t: Enemy): void => {
  strike(s, h, t);
  h.forced = false;
};

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0);

/** The walkable cell centre nearest to (x, y) within `r` (null: none). */
const floorNear = (s: BattleState, x: number, y: number, r: number, ok: (p: { x: number; y: number }) => boolean = () => true): { x: number; y: number } | null => {
  let best: { x: number; y: number } | null = null;
  let bd = 0x7fffffff;
  const R = s.map.radius;
  for (let c = 0, n = geo(R).q.length; c < n; c++) {
    if (!heroWalkable(s, c)) continue;
    const p = cellCenter(R, c);
    if (!withinRange(p.x, p.y, x, y, r) || !ok(p)) continue;
    const d = dist2(p.x, p.y, x, y);
    if (d < bd) {
      bd = d;
      best = p;
    }
  }
  return best;
};

/** Off the spots of magma warned under the hero: the nearest floor out of every one. True if it had to. */
const offMagma = (s: BattleState, h: Hero, spots: { x: number; y: number; t: number }[], r: number): boolean => {
  const near = spots.filter((p) => withinRange(h.x, h.y, p.x, p.y, r + 250));
  if (!near.length) return false;
  const p = floorNear(s, h.x, h.y, r * 3, (c) => !spots.some((o) => withinRange(c.x, c.y, o.x, o.y, r + 400)));
  if (!p) return false;
  h.target = 0;
  go(s, h, p.x, p.y, 100);
  return true;
};

/* ---------- the deep road ---------- */

export const ventBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.vent;
  const w = s.senda?.vent;
  if (!r || !w) return;
  const g = s.senda?.npcs.find((n) => n.id === 'grillo');
  if (!knows) {
    const t = nearestTo(enemiesNear(s, h.x, h.y, 5000, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return go(s, h, r.mouth.x, r.mouth.y);
  }
  // In a band warned or blowing: out of it, on Grillo's side.
  const i = ventAt(r, h.y);
  if (i >= 0) {
    const v = r.vents[i] as (typeof r.vents)[number];
    if (ventWarned(s, r, v) || ventBlowing(s, r, v)) {
      const past = !!g && g.y > v.y1;
      const p = floorNear(s, h.x, past ? v.y1 + 700 : v.y0 - 700, 1400);
      if (p) {
        h.target = 0;
        return go(s, h, p.x, p.y, 100);
      }
    }
  }
  // What is within reach, from where it stands (a ranged hero needn't walk into a band after it).
  const t = nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 200, (e) => targetable(s, e)), h.x, h.y);
  const near = nearestTo(enemiesNear(s, h.x, h.y, 1400, (e) => targetable(s, e)), h.x, h.y);
  if (near) return shoot(s, h, near);
  if (t) return shoot(s, h, t);
  // Out ahead to meet what comes up the road before it gets to Dunna (never into a band warned or blowing).
  const d = npc(s, 'dunna');
  const at = d ?? g;
  const foe = at ? nearestTo(enemiesNear(s, at.x, at.y, 6500, (e) => targetable(s, e)), at.x, at.y) : null;
  if (foe) {
    const lo = h.y < foe.y ? h.y : foe.y;
    const hi = h.y < foe.y ? foe.y : h.y;
    const hot = r.vents.some((v) => v.y1 >= lo && v.y0 <= hi && (ventWarned(s, r, v) || ventBlowing(s, r, v)));
    if (!hot) {
      h.target = 0;
      return go(s, h, foe.x, foe.y + (foe.y > h.y ? -h.stats.range + 600 : h.stats.range - 600), 300);
    }
  }
  h.target = 0;
  // By Grillo: a step behind him as he waits, with him as he crosses.
  if (g) {
    const crossing = w.cross >= 0;
    const want = { x: g.x + 500, y: g.y + (crossing ? 300 : -350) };
    const p = heroWalkable(s, cellAtWorld(s, want.x, want.y)) ? want : { x: g.x, y: g.y - 350 };
    // (Never into a band that is warned or blowing.)
    const j = ventAt(r, p.y);
    if (j >= 0 && j !== i && !crossing) {
      const v = r.vents[j] as (typeof r.vents)[number];
      if (ventWarned(s, r, v) || ventBlowing(s, r, v)) return;
    }
    go(s, h, p.x, p.y, 250);
  }
};

/* ---------- the river of embers ---------- */

export const emberBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.ember;
  const w = s.senda?.ember;
  if (!r || !w) return;
  const d = npc(s, 'dunna');
  if (!knows) {
    const t = nearestTo(enemiesNear(s, h.x, h.y, 9000, (e) => targetable(s, e)), h.x, h.y);
    if (t) return strike(s, h, t);
    h.target = 0;
    return;
  }
  if (offMagma(s, h, w.spots, r.pulse.r)) return;
  const at = d ?? h;
  const on = nearestTo(enemiesNear(s, at.x, at.y, 3200, (e) => targetable(s, e)), at.x, at.y);
  if (on) {
    if (withinRange(h.x, h.y, on.x, on.y, h.stats.range - 200)) return shoot(s, h, on);
    return strike(s, h, on);
  }
  h.target = 0;
  if (d) go(s, h, d.x + 300, d.y + 1500);
};

/* ---------- the trip-hammer's cams ---------- */

/** The faces the next turn has to strike: the rest of the run that holds the next face of the order. */
export const camsWant = (r: CamsRules, w: CamsState): number[] => {
  const runs = camsTurns(r.seq) ?? r.seq.map((f) => [f]);
  let k = 0;
  for (const run of runs) {
    if (w.done < k + run.length) return run.slice(w.done - k);
    k += run.length;
  }
  return [];
};

/** The blind bot's try: the first three faces of the order, all at once (the obvious). */
const blindWant = (r: CamsRules): number[] => r.seq.slice(0, 3);

/** One step toward cams on these faces: a wrong one off (straight to a place wanted, or back to its rack), then the missing. False when set. */
const camStep = (s: BattleState, h: Hero, r: CamsRules, w: CamsState, want: number[]): boolean => {
  if (w.job >= 0) return true;
  const send = (at: number): boolean => {
    camOrder(s, h, at);
    return true;
  };
  const on = camSet(w);
  const missing = want.filter((f) => !on.includes(f));
  if (w.hand >= 0) {
    const f = missing[0];
    if (f !== undefined) return send(f);
    return send(CAM_FACES + w.hand);
  }
  const wrong = on.find((f) => !want.includes(f));
  if (wrong !== undefined) return send(wrong);
  if (!missing.length) return false;
  for (let k = 0; k < w.on.length; k++) if ((w.on[k] as number) >= CAM_FACES) return send(w.on[k] as number);
  return false;
};

/** Where to stand to shoot La Tenaza in its side (`d` off its flank, the side nearer the hero), or null. */
const sideOf = (s: BattleState, h: Hero, e: Enemy, d: number): { x: number; y: number } | null => {
  const lean = turnOf(e.facing, iatan2(h.y - e.y, h.x - e.x)) >= 0 ? 1 : -1;
  for (const k of [lean, -lean]) {
    const a = e.facing + k * 256;
    const p = floorNear(s, e.x + idiv(cosA(a) * d, 10000), e.y + idiv(sinA(a) * d, 10000), 900);
    if (p) return p;
  }
  return null;
};

/** La Tenaza fought: from its side within reach, else round to it. */
const fightCrab = (s: BattleState, h: Hero, r: CamsRules, e: Enemy, knows: boolean): void => {
  if (!knows) return strike(s, h, e);
  const off = turnOf(e.facing, iatan2(h.y - e.y, h.x - e.x));
  const side = off > r.boss.front + 60 || off < -r.boss.front - 60;
  if (side && withinRange(h.x, h.y, e.x, e.y, h.stats.range - 200) && !withinRange(h.x, h.y, e.x, e.y, r.boss.reach + 200)) return shoot(s, h, e);
  const p = sideOf(s, h, e, imax(r.boss.reach + 700, h.stats.range - 700));
  if (!p) return shoot(s, h, e);
  h.target = 0;
  go(s, h, p.x, p.y, 200);
};

export const camsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.cams;
  const w = s.senda?.cams;
  if (!r || !w) return;
  const tz = w.tz;
  const crab = tz.mode === 1 || tz.mode === 2 ? findEnemy(s, tz.id) : undefined;
  const busy = w.job >= 0 && w.held * 2 > r.stand;
  if (knows && !busy) {
    // Out of the pincers about to shut.
    if (crab && tz.wind >= 0 && inArc(crab.x, crab.y, tz.aim, r.boss.reach + 400, r.boss.arc + 80, h.x, h.y)) {
      const a = tz.aim + (turnOf(tz.aim, iatan2(h.y - crab.y, h.x - crab.x)) >= 0 ? 400 : -400);
      const p = floorNear(s, crab.x + idiv(cosA(a) * 2200, 10000), crab.y + idiv(sinA(a) * 2200, 10000), 1200);
      if (p) {
        w.job = -1;
        h.target = 0;
        return go(s, h, p.x, p.y, 100);
      }
    }
    if (offMagma(s, h, w.spots, r.pulse.r)) {
      w.job = -1;
      return;
    }
    // Ash at it, or at Dunna: shot first.
    const ash = nearestTo(enemiesNear(s, h.x, h.y, 1600, (e) => targetable(s, e) && e.id !== tz.id), h.x, h.y);
    if (ash) {
      w.job = -1;
      return shoot(s, h, ash);
    }
    const d = npc(s, 'dunna');
    const atD = d ? nearestTo(enemiesNear(s, d.x, d.y, 2200, (e) => targetable(s, e) && e.id !== tz.id), d.x, d.y) : null;
    if (atD && w.job < 0) {
      if (withinRange(h.x, h.y, atD.x, atD.y, h.stats.range - 200)) return shoot(s, h, atD);
      return strike(s, h, atD);
    }
  }
  if (!knows) {
    const ash = nearestTo(enemiesNear(s, h.x, h.y, 1600, (e) => targetable(s, e)), h.x, h.y);
    if (ash && w.job < 0) return strike(s, h, ash);
  }
  if (w.freed >= 0) {
    if (crab) return fightCrab(s, h, r, crab, knows);
    const t = nearestTo(enemiesNear(s, h.x, h.y, 9000, (e) => targetable(s, e)), h.x, h.y);
    if (t) strike(s, h, t);
    return;
  }
  // The shaft turning, or held by the crab with the cams already set: the crab meanwhile.
  const want = knows ? camsWant(r, w) : blindWant(r);
  if (w.turn >= 0) {
    if (crab) return fightCrab(s, h, r, crab, knows);
    h.target = 0;
    return;
  }
  if (camStep(s, h, r, w, want)) return;
  if (w.hand >= 0) return;
  if (tenazaGrips(s)) {
    if (crab) return fightCrab(s, h, r, crab, knows);
    return;
  }
  camOrder(s, h, CAM_LEVER);
};
